import React from 'react';
import Hero from '../components/sections/Hero';
import AEOSection from '../components/sections/AEOSection';
import AuditForm from '../components/sections/AuditForm';
import CaseStudySection from '../components/sections/CaseStudySection';
import FAQSection from '../components/sections/FAQSection';
import { locations } from '../data/locations';
import { servicesData } from '../data/services';
import SafeIcon from '../components/common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { Link } from 'react-router-dom';
import SEO from '../components/common/SEO';

const Home = () => {
  const homeDefinition = "AlbertaRank is a specialized Answer Engine Optimization (AEO) agency that provides SEO for Alberta home service businesses. We utilize the Truth Protocol™ to ensure local companies rank #1 in the Google Map Pack and are recommended by AI assistants like ChatGPT and Google Gemini.";

  const homeFaqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How long does it take to rank for SEO in Calgary?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Typically, Calgary businesses see Map Pack movement in 30–90 days, depending on their 'Truth Signals' and review velocity managed by AlbertaRank."
        }
      },
      {
        "@type": "Question",
        "name": "What is the Truth Protocol™ in SEO?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The Truth Protocol™ is AlbertaRank's proprietary framework for aligning business data with local Alberta authorities, ensuring 100% entity trust for AI engines."
        }
      }
    ]
  };

  return (
    <>
      <SEO 
        title="AlbertaRank™ | AI-Driven SEO for Alberta Home Services" 
        description="Stop guessing. Start ranking. AlbertaRank provides the Truth Protocol™ for Calgary and Edmonton contractors to dominate search and AI."
        schemas={[homeFaqSchema]}
      />
      
      <Hero 
        title="Dominate Search & AI in Alberta"
        subtitle="AlbertaRank increases revenue for local trades by optimizing your business for the future of AI-driven search."
        definition={homeDefinition}
      />

      <AEOSection />

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="p-8 bg-slate-50 rounded-3xl border border-slate-100">
              <h3 className="text-xl font-bold mb-4">1. The Definition Block</h3>
              <p className="text-sm text-slate-600">We structure your site so AI models can pull "Direct Answers" about your business instantly.</p>
            </div>
            <div className="p-8 bg-slate-50 rounded-3xl border border-slate-100">
              <h3 className="text-xl font-bold mb-4">2. Semantic Triples</h3>
              <p className="text-sm text-slate-600">We write copy in Subject → Predicate → Object format, making it impossible for AI to misidentify your services.</p>
            </div>
            <div className="p-8 bg-slate-50 rounded-3xl border border-slate-100">
              <h3 className="text-xl font-bold mb-4">3. FAQ AI Bait</h3>
              <p className="text-sm text-slate-600">We target the exact questions homeowners ask AI assistants to ensure you are the cited source.</p>
            </div>
          </div>
        </div>
      </section>

      <CaseStudySection />

      <FAQSection />

      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-4">Complete Local Dominance</h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              AlbertaRank provides comprehensive growth systems for Alberta's best contractors.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {Object.entries(servicesData).map(([key, service]) => (
              <Link key={key} to={`/services/${key}`} className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-xl transition-all border border-gray-100 group">
                <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center text-orange-600 mb-6 group-hover:bg-orange-600 group-hover:text-white transition-colors">
                  <SafeIcon icon={service.icon} className="text-2xl" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{service.title}</h3>
                <p className="text-slate-600 leading-relaxed text-sm mb-4">{service.description}</p>
                <span className="text-orange-600 font-bold text-sm flex items-center gap-1">
                  Learn More <FiIcons.FiArrowRight />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Serving All of Alberta</h2>
              <p className="text-lg text-slate-600 mb-8">
                AlbertaRank proves business authority across all major Alberta municipalities.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {locations.map((loc) => (
                  <Link key={loc.slug} to={`/city/${loc.slug}`} className="flex items-center justify-between p-4 rounded-lg border border-gray-200 hover:border-orange-500 hover:bg-orange-50 transition-all group">
                    <span className="font-semibold text-slate-700 group-hover:text-orange-700">{loc.city} SEO</span>
                    <SafeIcon icon={FiIcons.FiArrowRight} className="text-gray-400 group-hover:text-orange-500" />
                  </Link>
                ))}
              </div>
            </div>
            <div className="relative">
              <AuditForm title="Get Your Truth Protocol™ Audit" />
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;